import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import s from './SecondScreen.module.css';

gsap.registerPlugin(ScrollTrigger);

const VALUES = [
  { title: 'Total Design', subtitle: 'תחת קורת גג אחת', desc: 'תכנון אדריכלי ועיצוב פרטי הפנים כמכלול אחד שלם ומגובש. מהקונספט הראשוני, דרך בחירת החומרים, הגוונים והגימורים ועד לפרט האחרון.', img: '/hero-images/gila-dining.webp' },
  { title: 'אדריכלית של שטח', subtitle: 'נוכחות מול בעלי המקצוע', desc: 'נוכחות קבועה ובולטת באתר הבניה. מלווה ומדריכה את הקבלנים ובעלי המקצוע במהלך העבודה ונותנת מענה רציף לפתרון בעיות שצצות בשטח.', img: '/hero-images/gal-13.webp' },
  { title: 'הקשבה לחזון', subtitle: 'נכנסת לראש שלכם', desc: 'מקשיבה לסיפור שמאחורי הבית, מדייקת את החזון ומתרגמת אותו לשפה אדריכלית ברורה ומדויקת - כזו שמשקפת אתכם בכל חלל.', img: '/hero-images/zohar-kitchen.webp' },
  { title: 'יוקרה שקטה', subtitle: 'עיצובים הרמוניים ורכים', desc: 'עיצוב שנראה טבעי, לא מתאמץ ואלגנטי.', img: '/hero-images/sivan-8.webp' },
  { title: 'אין אי אפשר', subtitle: 'תמיד נמצא את הדרך', desc: 'גם מול אתגרים מורכבים ומצבים מוגמרים, תמיד יש דרך ויש אפשרויות, השאלה רק איך ומה ההשלכות. תכנון חכם ויצירתי מביא כל חזון למימוש מלא.', img: '/hero-images/gal-living.webp' },
];

const TOTAL = VALUES.length;
const ORBIT_R = 330;

export default function SecondScreen() {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const orbRef = useRef(null);
  const headerRef = useRef(null);
  const ringRef = useRef(null);
  const progressRef = useRef(null);
  const imageRefs = useRef([]);
  const prevIdx = useRef(0);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!sectionRef.current || !pinRef.current) return;

    const ctx = gsap.context(() => {
      // Entrance
      const tl = gsap.timeline({
        scrollTrigger: { trigger: sectionRef.current, start: 'top 80%', toggleActions: 'play none none reverse' }
      });
      tl.fromTo(headerRef.current,
        { opacity: 0, y: 30, clipPath: 'inset(100% 0 0 0)' },
        { opacity: 1, y: 0, clipPath: 'inset(0% 0 0 0)', duration: 0.9, ease: 'power3.out' })
        .fromTo(orbRef.current,
          { scale: 0.85, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1.2, ease: 'power3.out' }, '-=0.5')
        .fromTo(ringRef.current,
          { scale: 0.9, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1, ease: 'power3.out' }, '-=0.8');

      // Pin the inner wrapper and scrub through values
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top',
        end: `+=${window.innerHeight * 3}`,
        pin: pinRef.current,
        scrub: 0.8,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const idx = Math.min(Math.floor(self.progress * TOTAL), TOTAL - 1);
          setActive(idx);
          if (ringRef.current) ringRef.current.style.transform = `rotate(${self.progress * 72}deg)`;
          if (progressRef.current) {
            const circ = 2 * Math.PI * ORBIT_R;
            progressRef.current.style.strokeDashoffset = circ - (self.progress * circ);
          }
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Image swap with circle clip-path
  useEffect(() => {
    const prev = prevIdx.current;
    if (prev === active) return;
    const prevImg = imageRefs.current[prev];
    const nextImg = imageRefs.current[active];
    if (!prevImg || !nextImg) return;

    gsap.set(nextImg, { clipPath: 'circle(0% at 50% 50%)', zIndex: 3 });
    gsap.to(nextImg, { clipPath: 'circle(75% at 50% 50%)', duration: 0.9, ease: 'power3.inOut' });
    gsap.to(prevImg, { scale: 1.06, duration: 0.9, ease: 'power2.inOut', onComplete: () => { gsap.set(prevImg, { zIndex: 1, scale: 1 }); gsap.set(nextImg, { zIndex: 2 }); } });
    prevIdx.current = active;
  }, [active]);

  const dotPos = (i) => {
    const angle = (-90 + (i / TOTAL) * 360) * (Math.PI / 180);
    return { x: Math.cos(angle) * ORBIT_R, y: Math.sin(angle) * ORBIT_R };
  };

  return (
    <section className={s.section} ref={sectionRef} id="approach">
      <div className={s.pin} ref={pinRef}>
        {/* Header */}
        <div className={s.headerArea}>
          <h2 className={s.headerTitle} ref={headerRef}>הגישה שלנו</h2>
        </div>

        {/* Stage */}
        <div className={s.stage}>
          {/* Ring */}
          <div className={s.ringWrap} ref={ringRef}>
            <svg className={s.ringSvg}
              viewBox={`${-ORBIT_R - 30} ${-ORBIT_R - 30} ${(ORBIT_R + 30) * 2} ${(ORBIT_R + 30) * 2}`}>
              <circle cx="0" cy="0" r={ORBIT_R} fill="none" stroke="rgba(184,151,106,0.25)" strokeWidth="1" />
              <circle ref={progressRef} cx="0" cy="0" r={ORBIT_R} fill="none" stroke="var(--gold)"
                strokeWidth="1.5" strokeDasharray={2 * Math.PI * ORBIT_R} strokeDashoffset={2 * Math.PI * ORBIT_R}
                strokeLinecap="round" transform="rotate(-90)" className={s.progressArc} />
              {VALUES.map((_, i) => {
                const p = dotPos(i);
                return (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r={active === i ? 6 : 3}
                      fill={active === i ? 'var(--gold)' : 'rgba(184,151,106,0.45)'} className={s.dot} />
                    {active === i && <circle cx={p.x} cy={p.y} r="14" fill="none" stroke="var(--gold)"
                      strokeWidth="1" opacity="0.4" className={s.dotPulse} />}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Orb */}
          <div className={s.orb} ref={orbRef}>
            <div className={s.orbInner}>
              {VALUES.map((v, i) => (
                <div key={i} ref={el => imageRefs.current[i] = el} className={s.orbSlide}
                  style={{ zIndex: i === 0 ? 2 : 1 }}>
                  <img src={v.img} alt={v.title} className={s.orbImg} loading={i === 0 ? 'eager' : 'lazy'} />
                </div>
              ))}
              <div className={s.orbOverlay} />
            </div>
            <div className={s.orbCounter}>
              <span className={s.orbNum}>{String(active + 1).padStart(2, '0')}</span>
              <span className={s.orbSep}>/</span>
              <span className={s.orbTotal}>{String(TOTAL).padStart(2, '0')}</span>
            </div>
          </div>
        </div>

        {/* Text */}
        <div className={s.textArea}>
          <div className={s.textContent} key={active}>
            <h3 className={s.valTitle}>{VALUES[active].title}</h3>
            <p className={s.valDesc}>{VALUES[active].desc}</p>
          </div>
          <div className={s.scrollHint}><div className={s.scrollDot} /></div>
        </div>
      </div>
    </section>
  );
}
